
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { UserState } from '@/composables/User';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';
import { getUserLocale } from '@/composables/user/Users';
import { formatAmount } from '@/components/table-rest-api/formatters';

export default defineComponent({
  name: 'ProductsV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const idUser = UserState.user?.id;
    const userType = UserState.user?.userType;
    const userLocale = ref<string>('en-US');

    // Load user locale asynchronously
    getUserLocale(idUser, userType).then((locale) => {
      userLocale.value = locale || 'en-US';
    });

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/products',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'product_id',
        header: '',
        fields: [{
          name: 'product_id',
          label: t('myShopData.products.headers.id_product'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'product_info',
        header: t('myShopData.products.headers.name'),
        fields: [{
          name: 'name',
          label: t('myShopData.products.headers.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.products.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.products.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'reference',
        header: t('myShopData.products.headers.reference'),
        fields: [{
          name: 'reference',
          label: t('myShopData.products.headers.reference'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'image',
        header: t('myShopData.products.headers.image_link'),
        fields: [{
          name: 'image_link',
          label: t('myShopData.products.headers.image_link'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'link',
        header: t('myShopData.products.headers.link'),
        fields: [{
          name: 'link',
          label: t('myShopData.products.headers.link'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'pricing',
        header: t('myShopData.products.headers.price'),
        fields: [{
          name: 'price',
          label: t('myShopData.products.headers.price'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'price_discount',
          label: t('myShopData.products.headers.price_discount'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'currency',
          label: t('myShopData.products.headers.currency'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.products.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.products.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.products.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.products.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('active'), value: true },
              { label: t('inactive'), value: false },
            ],
          },
        }],
      },
    ];

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO: Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/products',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.products.export.success'));
        // } else {
        //   await showToastError(t('myShopData.products.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.products.export.error'));
      }
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Wrapper to use formatAmount with user locale
    const formatAmountWithLocale = (amount: string | number | null | undefined, currency: string | null | undefined): string => formatAmount(amount, currency, userLocale.value);

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      handleExport,
      cleanStr,
      formatAmount: formatAmountWithLocale,
    };
  },
});
